import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaEnvelope, FaMapMarkerAlt, FaBuilding, FaEdit, FaSave, FaTimes, FaSpinner } from "react-icons/fa";
import { useTheme } from "../../../context/SocketContext";
import PageHeader from "../../../components/PageHeader";
import { Skeleton } from "../../../components/ui/Skeleton";
import { toast } from "../../../components/ui/Toast";
import { usePortalContent, getPortalIcon, getAccountPageMeta } from "../../../portal";

const ProfilePage = () => {
  const { currentThemeColors } = useTheme();
  const { content: profileContent } = usePortalContent('account-profile');
  const pageMeta = getAccountPageMeta(profileContent, 'profile');
  const ProfileIcon = getPortalIcon(pageMeta.icon);
  const [userData, setUserData] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [userAddress, setUserAddress] = useState("");
  const [userOrganization, setUserOrganization] = useState("");
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [isProfileEditing, setIsProfileEditing] = useState(false); 
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfileData = async () => {
      const authToken = localStorage.getItem("token");

      if (!authToken) {
        navigate("/login");
        return;
      }

      try {
        const profileResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (!profileResponse.ok) {
          throw new Error("Failed to fetch user profile data.");
        }

        const profileData = await profileResponse.json();
        if (profileData.user) {
          setUserData(profileData.user);
          setUserAddress(profileData.user.address || "");
          setUserOrganization(profileData.user.organization || "");
        } else {
          throw new Error("Invalid user profile data.");
        }
      } catch (error) {
        setProfileError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        navigate("/login");
      }
    };

    fetchUserProfileData();
  }, [navigate]);

  const handleProfileSave = async () => {
    setIsProfileSaving(true);
    const authToken = localStorage.getItem("token");

    if (!authToken) {
      setProfileError("Session expired. Please log in again.");
      localStorage.removeItem("token");
      navigate("/login");
      return;
    }

    try {
      const updateResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/updateProfile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          address: userAddress,
          organization: userOrganization,
        }),
      });

      if (!updateResponse.ok) {
        throw new Error("Failed to update user profile.");
      }

      const updateData = await updateResponse.json();
      if (updateData.success) {
        setUserData({ ...userData, address: userAddress, organization: userOrganization });
        setIsProfileEditing(false); 
        setProfileError("");
        toast.success("Profile updated successfully.");
      } else {
        throw new Error("Error updating user profile.");
      }
    } catch (error) {
      setProfileError(error.message);
      toast.error(error.message);
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handleProfileEdit = () => {
    setIsProfileEditing(true); 
  };

  const handleProfileCancel = () => {
    setIsProfileEditing(false); 
    setUserAddress(userData.address || ""); 
    setUserOrganization(userData.organization || "");
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ProfileIcon}
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
      />

      {/* Error Message */}
      {profileError && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30">
          <p className="text-red-600 dark:text-red-400 text-sm">{profileError}</p>
        </div>
      )}

      {/* Profile Content */}
      {userData ? (
        <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          <div className="space-y-6">
            {/* Read-only Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-dark-700 dark:text-gray-300">
                  <FaUser className="w-4 h-4 text-primary-500" />
                  Full Name
                </label>
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                  <p className="text-dark-800 dark:text-white font-medium">{userData.name}</p>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-dark-700 dark:text-gray-300">
                  <FaEnvelope className="w-4 h-4 text-primary-500" />
                  Email Address
                </label>
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                  <p className="text-dark-800 dark:text-white font-medium">{userData.email}</p>
                </div>
              </div>
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Address */}
              <div className="space-y-2">
                <label htmlFor="profileAddress" className="flex items-center gap-2 text-sm font-semibold text-dark-700 dark:text-gray-300">
                  <FaMapMarkerAlt className="w-4 h-4 text-primary-500" />
                  Address
                </label>
                <input
                  type="text"
                  id="profileAddress"
                  value={userAddress}
                  onChange={(e) => setUserAddress(e.target.value)}
                  disabled={!isProfileEditing}
                  placeholder="Enter your address"
                  className={`w-full p-4 rounded-xl border text-dark-800 dark:text-white placeholder-gray-400 transition-all duration-200 ${
                    isProfileEditing 
                      ? 'bg-white dark:bg-dark-800 border-primary-300 dark:border-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500' 
                      : 'bg-gray-50 dark:bg-dark-700 border-gray-200 dark:border-dark-600 cursor-not-allowed'
                  }`}
                />
              </div>

              {/* Organization */}
              <div className="space-y-2">
                <label htmlFor="profileOrganization" className="flex items-center gap-2 text-sm font-semibold text-dark-700 dark:text-gray-300">
                  <FaBuilding className="w-4 h-4 text-primary-500" />
                  Organization
                </label>
                <input
                  type="text"
                  id="profileOrganization"
                  value={userOrganization}
                  onChange={(e) => setUserOrganization(e.target.value)}
                  disabled={!isProfileEditing}
                  placeholder="Enter your organization"
                  className={`w-full p-4 rounded-xl border text-dark-800 dark:text-white placeholder-gray-400 transition-all duration-200 ${
                    isProfileEditing 
                      ? 'bg-white dark:bg-dark-800 border-primary-300 dark:border-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500' 
                      : 'bg-gray-50 dark:bg-dark-700 border-gray-200 dark:border-dark-600 cursor-not-allowed'
                  }`}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 border-t border-gray-100 dark:border-dark-700">
              {isProfileEditing ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleProfileSave}
                    disabled={isProfileSaving}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isProfileSaving ? (
                      <>
                        <FaSpinner className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleProfileCancel}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200"
                  >
                    <FaTimes className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleProfileEdit}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105"
                  style={{ 
                    background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
                    boxShadow: `0 10px 15px -3px var(--glow-color)`
                  }}
                >
                  <FaEdit className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="card p-12">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-20 w-20 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <p className="sr-only">Loading profile data...</p>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;